import { extendTheme } from "@mui/material/styles";

import fontFace, { MONO, SANS } from "./fonts.js";

/**
 * Kernel, powered by Lopie One.
 *
 * ## Brand colour is not a semantic colour
 *
 * The build map already uses green for "built", amber for "partly built" and
 * red for "blocked", and this console uses the same three for permitted,
 * needs-attention and refused. If the brand accent were also green, a green
 * chip would mean "this is permitted" in one place and "this is Kernel" in
 * another, and the semantic one would stop reading as a signal.
 *
 * So the brand is teal and the semantics are left alone. Teal also happens to
 * suit the subject — it reads as instrument rather than as marketing, and it
 * holds contrast on both a near-white and a near-black ground, which a lighter
 * accent would not.
 *
 * ## Neutrals are chosen, not inherited
 *
 * A pure mid-grey reads as nobody decided. These carry a slight cool bias
 * toward the accent, which is what makes a dense table of mostly-grey read as
 * designed rather than as unstyled.
 *
 * ## v9 notes
 *
 * `extendTheme` with palettes under `colorSchemes`, not `createTheme`. The
 * provider no longer takes `enableColorScheme` — that moved to `CssBaseline`.
 */

const brand = {
  50: "#e6f4f6",
  100: "#bde3e8",
  200: "#8ecdd6",
  300: "#5ab5c2",
  400: "#31a0b1",
  500: "#0e7490",
  600: "#0b5f78",
  700: "#084a5e",
  800: "#063845",
  900: "#04262f",
};

/** Named for what they mean, not for what colour they are.
 *
 * A screen writing `success.main` for a permitted tag is a screen that will
 * eventually use `success.main` for something else and quietly overload it.
 * These are the four states governance actually has. */
/**
 * Colours for telling one writer from another, which is not a state.
 *
 * ## Why this is a ramp rather than a rainbow
 *
 * Six writers want six hues and this product cannot spend them. Green, red,
 * amber and violet mean permitted, refused, attention and unattended, and a
 * chart that drew `xero` in green would make a permitted tag stop reading as
 * a signal three screens away. Semantic colour only works while it is scarce,
 * and a categorical palette is exactly the thing that stops it being scarce.
 *
 * So these are steps along the brand ramp plus one neutral. Nothing is ever
 * teal because it is *permitted*, so nothing teal can be misread as a state.
 *
 * Steps are picked for distance rather than for order — 700, 300, 900, 200 —
 * because two adjacent stops of one ramp are two shades of the same idea and
 * the whole job here is telling them apart. The legend carries the name and
 * the count regardless, which is where the work actually happens: colour is
 * the fourth way to distinguish a series here, not the first.
 */
const series = {
  // Alternating dark and light rather than walking down the ramp, because
  // these stack directly on top of each other and two adjacent stops read as
  // one band. The extremes are left out on purpose: `900` renders as black
  // and `50` as white, and a series that looks like the text or the paper
  // does not look like a series.
  light: [brand[600], brand[200], brand[400], "#5b6b70", brand[100], brand[800]],
  dark: [brand[200], brand[600], brand[400], "#9aa1a0", brand[100], brand[700]],
};

/**
 * Colours for a chart's series (LO-218), which is not `series` above.
 *
 * `series` tells six writers apart on a stacked bar and is built from the brand
 * ramp so that no hue is ever mistaken for a state. A chart over whatever a
 * connector brought in needs hues people can tell apart without a legend in
 * front of them, and the brand ramp cannot do that. So these are four hues,
 * each stepped for its own surface, taken from a palette that was run through
 * the data-viz validator and chosen to stay clear of the four state colours
 * above: no green, no red, no amber and no violet, because a series that
 * looked like `permitted` or `refused` would be read as one.
 *
 * Four, and a neutral. A fifth series is never a fifth hue: it folds into
 * `other`, or the chart is split. That is the method's rule for categorical
 * colour and it is the honest one here, since a fifth blue-ish thing is a
 * thing nobody can tell from the fourth.
 *
 * ## The order is the safety, not decoration
 *
 * All 24 orders of these four were run through the validator in both modes and
 * only four passed the colour-blind separation gate for adjacent series;
 * `blue, orange, aqua, magenta` was not one of them, because magenta beside
 * aqua cannot be told apart in a deuteranope's dark mode (delta E 1.6 against a
 * floor of 6). This is one that passed: magenta, blue, orange, aqua. Light mode
 * keeps two slots under 3:1 against the surface, which the method allows only
 * where the values are also in a table, and `Chart` always offers one.
 *
 * `single` is the brand primary: one series is the headline and takes the
 * product's own colour, so a lone chart is never orange for no reason.
 */
const chart = {
  light: {
    s1: "#e87ba4", s2: "#2a78d6", s3: "#eb6834", s4: "#1baf7a",
    other: "#6a706e", single: brand[500],
  },
  dark: {
    s1: "#d55181", s2: "#3987e5", s3: "#d95926", s4: "#199e70",
    other: "#9aa1a0", single: brand[300],
  },
};

const semantic = {
  light: {
    permitted: "#1f7a3d",
    refused: "#a32b2b",
    attention: "#b3730b",
    unattended: "#5b53b8",
    stale: "#6a706e",
  },
  dark: {
    permitted: "#4fb474",
    refused: "#e06666",
    attention: "#d9962a",
    unattended: "#9a92e8",
    stale: "#9aa1a0",
  },
};

const theme = extendTheme({
  cssVarPrefix: "kernel",

  // Without this the toggle in the app bar is decorative, and it was.
  //
  // The default selector is `media`, which means the dark palette is emitted
  // only inside `@media (prefers-color-scheme: dark)`. Following the system is
  // then the *only* thing that works: `setMode("dark")` writes `mui-mode` to
  // localStorage, `useColorScheme()` cheerfully reports `dark`, and not one
  // CSS variable moves, because nothing stamps a selector for the dark block
  // to match. Found in LO-31, by a mounted micro-app going dark while the
  // console around it stayed light — the remote was reading the mode the host
  // reported, and the host was not applying it.
  //
  // `data` stamps `data-light` / `data-dark` on `<html>`, which is what the
  // generated CSS keys off. "System" is not a fourth state in the DOM — MUI
  // resolves it and stamps whichever the machine actually is — so all three
  // states the toggle offers now do something, rather than one.
  colorSchemeSelector: "data",

  colorSchemes: {
    light: {
      palette: {
        primary: { main: brand[500], light: brand[300], dark: brand[700] },
        background: { default: "#f6f8f8", paper: "#ffffff" },
        text: { primary: "#12191a", secondary: "#5a6566" },
        divider: "#dfe5e5",
        kernel: semantic.light,
        series: series.light,
        chart: chart.light,
      },
    },
    dark: {
      palette: {
        primary: { main: brand[300], light: brand[200], dark: brand[600] },
        background: { default: "#0e1416", paper: "#151d1f" },
        text: { primary: "#e7eded", secondary: "#98a5a5" },
        divider: "#26312f",
        kernel: semantic.dark,
        series: series.dark,
        chart: chart.dark,
      },
    },
  },

  shape: { borderRadius: 8 },

  // ## The scale had no top end
  //
  // `h1` was 1.5rem and body was 0.875rem: the whole product lived inside a
  // 1.7x range, which meant nothing could be emphatic. Every screen answers
  // one question — *how much of this happened without a person*, *what has
  // this agent cost* — and the answer was set two points larger than the
  // caption under it.
  //
  // `display` is the top end and it is used **once per screen**, for the
  // figure the screen is named after. A second one on the same page is two
  // headlines and therefore none. `lede` is the sentence under it.
  //
  // Dense is still the default below that. This is a tool, and a tool that
  // needs scrolling to show twelve rows is a tool people stop opening — the
  // top of the scale exists so the bottom of it can stay tight.
  typography: {
    fontFamily: SANS,
    fontSize: 14,

    display: {
      fontFamily: SANS,
      fontSize: "clamp(2rem, 1.2rem + 2.6vw, 3rem)",
      // Light, not heavy. A big number in 600 shouts; the same number in 300
      // with room around it is simply large, which is the register this
      // product wants — an instrument reading, not a headline.
      fontWeight: 300,
      lineHeight: 1.05,
      letterSpacing: "-0.03em",
      // These are numbers that get compared against last week's numbers.
      fontVariantNumeric: "tabular-nums",
    },
    lede: {
      fontFamily: SANS,
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.5,
    },

    h1: { fontSize: "1.375rem", fontWeight: 600, letterSpacing: "-0.015em" },
    h2: { fontSize: "1.0625rem", fontWeight: 600, letterSpacing: "-0.01em" },
    overline: {
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      lineHeight: 1.6,
    },
    // Ids, tokens and timestamps. Anything you might compare character by
    // character wants a face where you can — and Plex Mono slashes its zero
    // and separates `1lI`, which is the reason to carry a mono at all.
    mono: {
      fontFamily: MONO,
      fontSize: "0.8125rem",
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: (themeParam) => `
        ${fontFace}

        /* Wide tables scroll inside their own container; the page never
           scrolls sideways. */
        body {
          overflow-x: hidden;
          /* Plex reads better with its default spacing at text sizes and
             slightly tightened at display sizes, which the variants handle.
             This is the one global: kerning and ligatures on, because they
             are off by default in some engines below 20px. */
          font-kerning: normal;
          -webkit-font-smoothing: antialiased;
        }

        /* A focus ring that is visible on both grounds and is not the
           browser's default blue, which reads as "not styled". Keyboard only:
           a mouse click should not leave a ring behind it. */
        :focus-visible {
          outline: 2px solid ${themeParam.vars.palette.primary.main};
          outline-offset: 2px;
          border-radius: 3px;
        }
      `,
    },
    MuiPaper: {
      styleOverrides: {
        // ## Three surfaces, not one
        //
        // Every panel in this product was `variant="outlined"`: the same
        // hairline, the same radius, the same padding, from a thirteen-card
        // register to the one number a screen is about. Nothing could be
        // more important than anything else, because nothing was allowed to
        // look it.
        //
        // `outlined` stays the workhorse and gets a slightly softer border,
        // because a grid of them at full-strength divider colour reads as
        // graph paper. `elevation` is the raised one and it is a *tint*
        // rather than a shadow — a drop shadow on a dense console is the
        // thing that makes it look like a 2014 dashboard.
        outlined: {
          borderColor: "color-mix(in srgb, currentColor 9%, transparent)",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          // Motion on change, never on load. A button is the one control
          // that always means something happened.
          transition: "background-color 120ms, border-color 120ms",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          // Chips sat at the same radius as the cards holding them, so a row
          // of tags read as a row of tiny cards. A pill is a different kind
          // of object and should say so.
          borderRadius: 999,
        },
        // Chips carry tags and ids. Both are compared, not read.
        labelSmall: { fontSize: "0.75rem" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: 9,
          paddingBottom: 9,
          // The hairline between rows was the full divider, which on a fifty
          // row table is fifty full-strength lines and reads as a fence. A
          // table wants rhythm, not a grid.
          borderColor: "color-mix(in srgb, currentColor 8%, transparent)",
        },
        head: {
          fontSize: "0.6875rem",
          fontWeight: 600,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          // Reading across a wide row is the thing a log costs you, and a
          // hover band is the cheapest fix there is.
          "&:hover": {
            backgroundColor: "color-mix(in srgb, currentColor 3%, transparent)",
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        // Tooltips carry whole sentences in this product — the basis of a
        // number, what a refusal means — and the default 300px turns those
        // into six-line slivers.
        tooltip: { maxWidth: 380, fontSize: "0.8125rem", lineHeight: 1.5 },
      },
    },
    MuiLink: {
      defaultProps: { underline: "hover" },
    },
  },
});

export default theme;
